import { FC, useState } from 'react';
import { ILeftColumn } from '../../leftColumn.component';
import {
  Button,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { AddressHover } from './components/addressHover.component';
import { orderDetailsState } from '../../../../../../../pages/order/edit/states/orderDetails/orderDetails.state';

export const Address: FC<TAddress> = (props) => {
  const { addressValue, addressNameField, register, readOnlyAddress, isAddressPinned } = props;
  const { t } = useTranslation();
  const isFullHd = useMediaQuery('(min-width:1920px)');

  const [open, setOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setOpen(true);
  };

  const handleMouseLeave = (event?: React.MouseEvent) => {
    // If the mouse is on the pin button, do not close the tooltip
    if (event) {
      const relatedTarget = event?.relatedTarget as HTMLElement;
      if (relatedTarget && relatedTarget?.closest?.('[data-pin-button]')) {
        return;
      }
    }

    const id = setTimeout(() => {
      setOpen(false);
    }, 500);
    setTimeoutId(id);
  };

  const handlePinClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    orderDetailsState.pub.updateIsAddressPinned(true, isFullHd);
    setOpen(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const isPinnedButtonExists = typeof isAddressPinned === 'boolean';
  const needShowAddressField = !(isPinnedButtonExists && isAddressPinned);

  return (
    <>
      {needShowAddressField && (
        <STooltipWrapper
          title={
            <AddressHover
              {...{
                addressValue,
                addressNameField,
                register,
                readOnlyAddress,
                isAddressPinned,
                handlePinClick,
                handleMouseEnter,
                handleMouseLeave,
                isPinnedButtonExists,
              }}
            />
          }
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          open={open}
          placement='bottom-start'
        >
          <Button sx={{ marginTop: 1 }} startIcon={<LocationCityIcon />}>
            {t('common.address')}
          </Button>
        </STooltipWrapper>
      )}
    </>
  );
};

const STooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 0,
    maxWidth: '500px',
    pointerEvents: 'auto', // allow interaction with elements inside tooltip
  },
}));

export type TAddress = Pick<
  ILeftColumn,
  'addressValue' | 'addressNameField' | 'register' | 'readOnlyAddress' | 'isAddressPinned'
>;
